import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Capacitor } from '@capacitor/core';
import { CallbackID, Geolocation, Position } from '@capacitor/geolocation';
import { select, Store } from '@ngrx/store';

import { isNull } from 'lodash-es';
import { asyncScheduler, BehaviorSubject, map, of, switchMap, tap, throttleTime } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';

import * as fromMapActions from '../../store/actions/map.actions';
import * as fromConfigSelectors from '../../store/selectors/config.selectors';

@Injectable({
  providedIn: 'root'
})
export class MapsService {
  private store = inject(Store);
  private httpClient = inject(HttpClient);

  private isGoogleMapsAPILoaded = false;
  private watchPositionCallbackId: CallbackID | null = null;

  private userPosition: BehaviorSubject<Position | null> = new BehaviorSubject<Position | null>(null);
  private mapsUsable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor() {
    this.userPosition
      .pipe(
        filter((position) => !isNull(position)),
        throttleTime(1000, asyncScheduler, { leading: true, trailing: true })
      )
      .subscribe((position) => {
        this.store.dispatch(
          fromMapActions.updateUserPosition({
            payload: {
              location: {
                lat: position?.coords.latitude,
                lng: position?.coords.longitude
              }
            }
          })
        );
      });
  }

  isMapsUsable() {
    return this.mapsUsable.getValue();
  }

  getMapsUsable() {
    return this.mapsUsable.asObservable();
  }

  load() {
    if (!this.isGoogleMapsAPILoaded) {
      this.isGoogleMapsAPILoaded = true;
      return this.store.pipe(select(fromConfigSelectors.selectGoogleMapsAPIKey)).pipe(
        take(1),
        switchMap((apiKey) =>
          this.httpClient
            .jsonp(`https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,drawing,marker`, 'callback')
            .pipe(
              map(() => true),
              catchError(() => of(false)),
              tap((isGoogleMapsAPILoaded) => {
                this.isGoogleMapsAPILoaded = isGoogleMapsAPILoaded;
                this.mapsUsable.next(isGoogleMapsAPILoaded);
              })
            )
        )
      );
    } else {
      return true;
    }
  }

  async enableWatchPosition() {
    if (isNull(this.watchPositionCallbackId)) {
      const { location } = await Geolocation.checkPermissions();
      let permission = location;
      if (Capacitor.isNativePlatform() && permission !== 'granted') {
        const { location } = await Geolocation.requestPermissions({ permissions: ['location'] });
        permission = location;
      }

      if (!Capacitor.isNativePlatform() || permission === 'granted') {
        this.watchPositionCallbackId = await Geolocation.watchPosition(
          {
            enableHighAccuracy: true,
            maximumAge: 5000
          },
          (position) => {
            this.userPosition.next(position);
          }
        );
      }
    }
  }

  async disableWatchPosition() {
    if (!isNull(this.watchPositionCallbackId)) {
      await Geolocation.clearWatch({ id: this.watchPositionCallbackId });
      this.watchPositionCallbackId = null;
    }
  }
}
